import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import moment from "moment";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";
import ReactTooltip from "react-tooltip";
import scheduleMods from "data/schedule-mods";
import getColumnWidth from "helpers/getColumnWidthReactTable";
import exportFromBlob from "helpers/export-from-blob";
import UserTypes from "components/auth/user-types";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import {
    closeCustomModal,
    openCustomModal
} from "components/custom/modals";

import {
    setLoading,
    fetchScheduleListPaged,
    clearScheduleList,
    setScheduleActivate,
    setScheduleDeactivate,
    deleteScheduleActivate,
    scheduleExport,
    requestPatchSchedule,
    requestPatchScheduling
} from "api/menu/schedule/actions";

import ScheduleFilters from "components/menu/schedule/filter/filters";
import ServiceConfirmationCell from "components/menu/schedule/service-confirmation-cell";

const ServiceReport = React.lazy(() =>
    import("components/menu/reporting/service-report"));

class ScheduleList extends React.Component {
    dateFormat = "YYYY-MM-DD";

    modalObj = false;

    constructor(props) {
        super(props);

        this.state = {
            tableState: {},
            page: 0,
            scheduleList: [],

            activeFilters: {
                earliestDate: moment().startOf("month").format("YYYY-MM-DD"),
                latestDate: moment().endOf("month").format("YYYY-MM-DD"),
            },

            requestParams: {
                mode: scheduleMods.Open.value,
            }
        };

        this.reactTable = React.createRef();

        this.filterResult = this.filterResult.bind(this);
        this.handleFilterCallback = this.handleFilterCallback.bind(this);
        this.searchByFilters = this.searchByFilters.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        this.getColumns = this.getColumns.bind(this);
        this.exportSchedules = this.exportSchedules.bind(this);
        this.handleTableChange = this.handleTableChange.bind(this);
        this.handleSortedChange = this.handleSortedChange.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
        this.onTableViewChange = this.onTableViewChange.bind(this);
    }

    componentDidMount() {
        this.handleTableChange({
            page: 0,
            pageSize: 20,
            sorted:[
                {
                    id: "earliestDate",
                    desc: false
                },
                {
                    id: "latestDate",
                    desc: false
                }
            ]
        });
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            scheduleList: nextProps.scheduleList
        }, this.onTableViewChange);
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.scheduleList, this.props.scheduleList)) {
            this.setState({
                scheduleList: this.props.scheduleList
            });
        }
    }

    componentWillUnmount () {
        this.props.clearScheduleList();
    }

    filterResult = (list, activeFilters) => list.filter((item) => {
        if (activeFilters.jobNumber && item.jobNumber !== activeFilters.jobNumber) {
            return false;
        }

        return !(activeFilters.merchandiser && item.merchandiser !== activeFilters.merchandiser);
    });

    handleFilterCallback = (filterKey, value) => {
        const activeFilters = Object.assign({}, this.state.activeFilters);
        activeFilters[filterKey] = value;

        this.setState({
            activeFilters
        });
    };

    checkCanSearch = (activeFilters, callback) => {
        if (activeFilters.earliestDate && activeFilters.latestDate) {
            callback();
        }
    };

    resetFilters(callback = false) {
        this.setState({
            activeFilters: {
                earliestDate: moment().startOf("month").format("YYYY-MM-DD"),
                latestDate: moment().endOf("month").format("YYYY-MM-DD"),
            }
        }, () => {
            if (typeof callback === "function") {
                callback();
            } else {
                this.searchByFilters();
            }
        });
    }

    searchByFilters() {
        this.checkCanSearch(this.state.activeFilters, () => {
            this.handleTableChange(this.state.tableState);
        });
    }

    handleTableChange(tableState) {
        const {
            user,
            userRole
        } = this.props;
        const {
            activeFilters,
            requestParams
        } = this.state;
        requestParams.pageNumber = tableState.page;
        requestParams.pageSize = tableState.pageSize;
        requestParams.sortOptions = null;

        const isMerchandiser = userRole.includes(UserTypes.merchandiser);
        requestParams.merchandiserIds = isMerchandiser ? [user.nameid] : null;

        // sorts
        if (tableState.sorted.length) {
            requestParams.sortOptions = {};
            tableState.sorted.forEach(item => {
                requestParams.sortOptions[item.id] = item.desc;
            });
        }

        // filters
        const notNeedFloatArr = ["traits", "exceptions", "category", "mode"];
        Object.keys(activeFilters).forEach((key) => {
            const parseFloatValue = notNeedFloatArr.indexOf(key) === -1;
            requestParams[key] = null;
            if (activeFilters[key] !== null && typeof activeFilters[key] === "string") {
                requestParams[key] = activeFilters[key];
            } else if (activeFilters[key] !== null && activeFilters[key].value) {
                requestParams[key] = activeFilters[key].value;
                if (parseFloatValue) {
                    requestParams[key] = parseFloat(requestParams[key]);
                }
            } else if (Array.isArray(activeFilters[key]) && activeFilters[key].length) {
                requestParams[key] = activeFilters[key].map(item => {
                    if (parseFloatValue) {
                        return parseFloat(item.value);
                    }
                    return item.value;
                });
            }
        });

        this.setState({
            requestParams,
            page: tableState.page,
            tableState: {
                page: tableState.page,
                pageSize: tableState.pageSize,
                sorted: tableState.sorted,
            },
        });

        this.props.fetchScheduleListPaged(requestParams);
    }

    handlePageChange = (pageIndex) => {
        const requestParams = Object.assign({}, this.state.requestParams);
        const tableState = Object.assign({}, this.state.tableState);
        requestParams.pageNumber = pageIndex + 1;
        tableState.page = pageIndex;
        this.setState({
            requestParams,
            tableState
        }, () => {
            this.handleTableChange(tableState);
        });
    };

    handleSortedChange = (sorted) => {
        this.handleTableChange({
            page: this.state.tableState.page,
            pageSize: this.state.tableState.pageSize,
            sorted
        });
    };

    handlePageSizeChange = (pageSize) => {
        this.handleTableChange({
            page: this.state.tableState.page,
            pageSize,
            sorted: this.state.tableState.sorted
        });
    };

    onTableViewChange = () => {
        const current = this.reactTable.current;
        if (current) {
            const scheduleList = current
                .getResolvedState()
                .sortedData.map(item => item._original);
            this.setState({ scheduleList });
        }
    };

    getColumns = () => {
        const {
            user
        } = this.props;
        const {
            scheduleList,
        } = this.state;

        // const classNameFlexCenter = "d-flex justify-content-center align-items-center";
        const classNameFlexCenter = "";

        let result = [];
        result.push({
            Header: "#",
            sortable:false,
            id: "show-enter-report",
            Cell: ({ original }) => (
                    <Button
                        className="p-1"
                        size="sm"
                        onClick={() => this.viewReport(original.serviceId)}
                    >
                        Show/Enter Report
                    </Button>
            ),
            className: classNameFlexCenter,
            width: 120
        });
/*
        result.push({
            Header: "Merchandiser",
            // accessor: "merchandiser",
            id: "merchandiser",
            accessor: original => (original.merchandiser === `${user.family_name}, ${user.given_name}` ? "Me" : original.merchandiser),
            Cell: ({ original }) => (
                <div className="w-100 d-flex justify-content-between">
                    <div>
                        {original.merchandiser === `${user.family_name}, ${user.given_name}` ? "Me" : original.merchandiser}
                    </div>

                    <div>
                        {original.merchandiserAvailability.status  !== "Available" ?
                            <FontAwesomeIcon
                                icon="info-circle"
                                size="lg"
                                data-tip
                                data-for={`worker-icon-${original.merchandiserId}`}
                                className="ml-2 mr-2"
                            />
                            : null}

                        {original.merchandiserAvailability.details ?
                            <ReactTooltip id={`worker-icon-${original.merchandiserId}`} effect="float">
                                <div className="text-left">
                                    <div className="border-bottom font-weight-bold">
                                        {original.merchandiserAvailability.status}
                                    </div>
                                    {original.merchandiserAvailability.details
                                        ? original.merchandiserAvailability.details
                                            .map((item, key) =>
                                                <div key={key}>
                                                    {item}
                                                </div>)
                                        : null
                                    }
                                </div>
                            </ReactTooltip>
                            : null}

                        {!original.isTemporary ||
                        <FontAwesomeIcon
                            icon="hourglass-half"
                            title="Temporary Assignment"
                            className="mr-2 ml-2"
                        />
                        }
                    </div>
                </div>
            ),
            getProps: (state, rowInfo) => ({
                className: rowInfo && rowInfo.original && rowInfo.original.employeeAssignedIdIsUpdated ? "m-0 schedule-updated" : ""
            }),
        });

        */
        result.push({
            Header: "Confirmation",
            id: "confirmation",
            className: classNameFlexCenter,
            width: 100,
            Cell: ({ original }) => (
                <ServiceConfirmationCell original={original} />
            ),
        });
        result.push({
            Header: "Earliest",
            id: "earliestDate",
            className: classNameFlexCenter,
            width: 100,
            getProps: (state, rowInfo) => ({
                className: rowInfo && rowInfo.original && rowInfo.original.earliestDateIsUpdated ? "m-0 schedule-updated" : ""
            }),
            Cell: ({ original }) => (
                <div className="mr-1">
                    {moment(original.earliestDate).format(this.dateFormat)}
                </div>
            ),
        });
        result.push({
            Header: "Latest",
            id: "latestDate",
            className: classNameFlexCenter,
            width: 100,
            getProps: (state, rowInfo) => ({
                className: rowInfo && rowInfo.original && rowInfo.original.latestDateIsUpdated ? "m-0 schedule-updated" : ""
            }),
            Cell: ({ original }) => (
                <div className="mr-1">
                    {moment(original.latestDate).format(this.dateFormat)}
                </div>
            ),
        });
        result.push({
            Header: "Retailer",
            accessor: "retailerName",
            id: "retailerName",
            width: 70
        });
        result.push({
            Header: "Store #",
            accessor: "storeNumber",
            id: "storeNumber",
            width: 60
        });
        result.push({
            Header: "Name",
            accessor: "storeName",
            id: "storeName",
        });
        result.push({
            Header: "Job #",
            accessor: "jobNumber",
            id: "jobNumber",
            width: 100
        });
        result.push({
            Header: "Product",
            accessor: "productName",
            id: "productName",
        });
        result.push({
            Header: "Limit",
            accessor: "timeLimit",
            id: "timeLimit",
            getProps: (state, rowInfo) => ({
                className: rowInfo && rowInfo.original && rowInfo.original.timeLimitIsUpdated ? "m-0 schedule-updated" : ""
            }),
            Cell: ({ original }) => (
                <div className="mr-1">
                    {original.timeLimit}
                </div>
            ),
        });
        result.push({
            Header: "Service",
            accessor: "serviceType",
            id: "serviceType",
            getProps: (state, rowInfo) => ({
                className: rowInfo && rowInfo.original && rowInfo.original.serviceTypeIdIsUpdated ? "m-0 schedule-updated" : ""
            }),
            Cell: ({ original }) => (
                <div className="mr-1">
                    {original.serviceType}
                </div>
            ),
        });
        result.push({
            Header: "Trait",
            accessor: "trait",
            id: "trait",
            Cell: ({ original }) => (
                <div className="mr-1">
                    {original.trait}
                </div>
            ),
        });

        result = result.map((value) => {
            value.headerClassName = "text-left";
            if (!value.style) {
                value.style = {};
            }
            if (value.style) {
                value.style.display = "flex";
                value.style.alignItems = "center";
                value.style.height = "100%";
            }
            if (value.id !== "show-enter-report") {
                value.width = getColumnWidth(
                    scheduleList,
                    value.accessor,
                    value.Header
                );
            }
            return value;
        });

        return result;
    };

    exportSchedules = () => {
        const { requestParams } = this.state;
        this.props.scheduleExport(requestParams, (schedulesForExport) => {
            const fileName = `schedules-${moment().format("YYYY-MM-DD")}.xlsx`;
            exportFromBlob(schedulesForExport, fileName);
        });
    };

    viewReport = (serviceId) => {
        const modalObj = {
            id: "view-report",
            modalSettings: {
                size: "lg",
                style: {
                    top: 20,
                    width: "calc(100% - 40px)"
                },
            },
            header: `Service Report: ${serviceId}`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ServiceReport
                        serviceId={serviceId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSave={() => {
                            this.props.closeCustomModal(modalObj);
                            this.handleTableChange(this.state.tableState);
                        }}
                        onSubmit={() => {
                            this.props.closeCustomModal(modalObj);
                            this.handleTableChange(this.state.tableState);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    render () {
        const {
            loading,
            user,
            userRole,
            scheduleListOptions
        } = this.props;

        const {
            scheduleList,
            activeFilters,
            page
        } = this.state;

        const isMerchandiser = userRole.includes(UserTypes.merchandiser);

        return (
            <div className="dashboard">
                <ScheduleFilters
                    title={`Schedule ${isMerchandiser ? `- ${user.given_name} ${user.family_name}` : ""}`}
                    titleFilteredCounts={{
                        totalFilteredRecords: scheduleListOptions.totalFilteredRecords || 0,
                        totalRecords: scheduleListOptions.totalRecords || 0
                    }}
                    filters={scheduleListOptions.metadata}
                    activeFilters={activeFilters}
                    filterCallback={this.handleFilterCallback}
                    onSearch={this.searchByFilters}
                    onReset={this.resetFilters}
                    onCheckCanSearch={this.checkCanSearch}
                    onExport={this.exportSchedules}
                    showMerchandisers={!isMerchandiser}
                />

                <ReactTable
                    data={scheduleList}
                    headerClassName="table-header"
                    columns={this.getColumns()}
                    defaultPageSize={20}
                    pageSizeOptions={[20, 50, 100, 500, 1000]}
                    pages={Math.ceil(
                        scheduleListOptions.totalFilteredRecords /
                        scheduleListOptions.pageSize
                    )}
                    page={page}
                    resizable
                    className="-striped -highlight fix-scrollbar-size schedules-table"
                    loading={loading}
                    ref={this.reactTable}
                    onFilteredChange={(filtered, column) =>
                        this.onTableViewChange(filtered, column)
                    }
                    filterable={false}
                    defaultFilterMethod={filterCaseInsensitive}
                    // onFetchData={this.handleTableChange}
                    onSortedChange={this.handleSortedChange}
                    onPageChange={this.handlePageChange}
                    onPageSizeChange={this.handlePageSizeChange}
                    handleRefreshPage={this.searchByFilters}
                    manual={true}
                    defaultSorted={[
                        {
                            id: "earliestDate",
                            desc: false
                        },
                        {
                            id: "latestDate",
                            desc: false
                        }
                    ]}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    style={{
                        height: "calc(100vh - 170px)",
                    }}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.row && rowInfo.row._original) {
                            return {
                                className: rowInfo.row._original.isHighlighted ? "m-0 schedule-yellow" : ""
                            };
                        }
                        return {};
                    }}
                />
            </div>
        );
    }
}

ScheduleList.propTypes = {
    fetchScheduleListPaged: PropTypes.func.isRequired,
    clearScheduleList: PropTypes.func.isRequired,
    scheduleList: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
    user: state.auth.user,
    userRole: state.auth.user.role,
    scheduleList: state.schedule.scheduleList,
    scheduleListOptions: state.schedule.scheduleListOptions,
    loading: state.schedule.loading
});

const mapStateToDispatch = dispatch => ({
    setLoading: bindActionCreators(setLoading, dispatch),
    fetchScheduleListPaged: bindActionCreators(fetchScheduleListPaged, dispatch),
    clearScheduleList: bindActionCreators(clearScheduleList, dispatch),
    setScheduleActivate: bindActionCreators(setScheduleActivate, dispatch),
    setScheduleDeactivate: bindActionCreators(setScheduleDeactivate, dispatch),
    scheduleExport: bindActionCreators(scheduleExport, dispatch),
    deleteScheduleActivate: bindActionCreators(deleteScheduleActivate, dispatch),
    requestPatchSchedule: bindActionCreators(requestPatchSchedule, dispatch),
    requestPatchScheduling: bindActionCreators(requestPatchScheduling, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ScheduleList);
